// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import type {Value} from "@swim/structure";
import {Uri} from "@swim/uri";
import {MapDownlink} from "@swim/client";
import {TraitModelSet} from "@swim/model";
import {RelationTrait} from "@nstream/domain";
import {AgentEntityTrait} from "./AgentEntityTrait";

/** @public */
export class AgentRelationTrait extends RelationTrait<AgentEntityTrait> {
  constructor() {
    super();
    this.id.set("agent");
    this.title.setIntrinsic("Agents");
  }

  @TraitModelSet({
    extends: true,
    traitType: AgentEntityTrait,
    initTrait(agentTrait: AgentEntityTrait): void {
      // Initialize the entity
      const agentId = agentTrait.id.value;
      if (agentId !== void 0 && agentTrait.nodeUri.value === null) {
        const metaNodeUri = Uri.fromInit({scheme: "swim", path: ["meta:node", "/", agentId]});
        agentTrait.nodeUri.setIntrinsic(metaNodeUri);
      }
      // Configure entity aspects
      agentTrait.portal.insertModel();
    },
  })
  override readonly entities!: TraitModelSet<this, AgentEntityTrait> & RelationTrait<AgentEntityTrait>["entities"];

  @MapDownlink({
    keyForm: Uri.form(),
    consumed: true,
    didUpdate(nodeUri: Uri, value: Value): void {
      //console.log("AgentRelationTrait.agents.didUpdate " + nodeUri + ":", value.toLike());
      const agentId = nodeUri.toString();
      let agentTrait = this.owner.entities.get(agentId);
      if (agentTrait === null) {
        agentTrait = this.owner.entities.createTrait(agentId);
        agentTrait.title.setIntrinsic(nodeUri.pathName);

        const isAgent = value.get("agents").isDefined();
        if (isAgent) {
          agentTrait.lanesRelation.insertModel();
        }

        const childCount = value.get("childCount").numberValue(0);
        if (childCount > 0) {
          agentTrait.agentsRelation.insertModel();
          const metaHostUri = Uri.fromInit({scheme: "swim", path: ["meta:host"]});
          const nodesLaneUri = Uri.fromInit({path: ["nodes"], fragment: agentId  + "/"});
          agentTrait.agentsRelation.trait!.nodeUri.set(metaHostUri);
          agentTrait.agentsRelation.trait!.laneUri.set(nodesLaneUri);
        }

        this.owner.entities.addTrait(agentTrait);
      }
    },
  })
  readonly agents!: MapDownlink<this, Uri, Value>;
}
